/* global t, safeArray, num */
// EGENKAPITAL — kontoene, oppstillingen og egenkapitaltransaksjonene
//
// Delene lesemodellen i backend-equity-recon-view.jsx bygger på. Filen eier
// kontobåndene, egenkapitaloppstillingen og transaksjonene bak den.
//
// Fortegnet er årsregnskapets. Egenkapital står med kredit i hovedboken, og
// saldobalansen bærer den som et negativt tall. Oppstillingen viser den positiv,
// slik `buildAnnualAccountsModel` gjør det (`{ sign: -1 }`) — to fortegnsregler
// for den samme egenkapitalen ville betydd at balansen og avstemmingen viste
// forskjellige tall for samme konto.

const equityReconRound = (value) => Math.round((num(value) + Number.EPSILON) * 100) / 100;

const equityReconMonthRange = (periodId) => {
  const [yearText, monthText] = String(periodId || '').split('-');
  const year = Number(yearText);
  const monthIndex = Number(monthText) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || monthIndex < 0 || monthIndex > 11) return null;
  const pad = (value) => String(value).padStart(2, '0');
  const lastDay = new Date(year, monthIndex + 1, 0).getDate();
  return {
    year,
    monthIndex,
    from: year + '-' + pad(monthIndex + 1) + '-01',
    to: year + '-' + pad(monthIndex + 1) + '-' + pad(lastDay),
    yearFrom: year + '-01-01',
  };
};

// Egenkapitalkategoriene, med kontobåndene fra kontoplanen appen faktisk bruker
// (`src/tax-income-balance-accounts.jsx`). Kategorien avgjør hvilken rad kontoen
// havner på i oppstillingen — ikke hva noen har kalt kontoen.
const EQUITY_RECON_CATEGORIES = [
  { key: 'aksjekapital', get label() { return t('rce.cat.shareCapital', 'Aksjekapital'); }, kind: 'innskutt', from: 2000, to: 2009 },
  { key: 'egne_aksjer', get label() { return t('avbe.capital.ownShares', 'Egne aksjer'); }, kind: 'innskutt', from: 2010, to: 2014 },
  { key: 'andelskapital', get label() { return t('rce.cat.cooperativeCapital', 'Felleseid andelskapital'); }, kind: 'innskutt', from: 2015, to: 2019 },
  { key: 'overkurs', get label() { return t('rce.cat.sharePremium', 'Overkurs'); }, kind: 'innskutt', from: 2020, to: 2029 },
  { key: 'annen_innskutt', get label() { return t('rce.cat.otherPaidIn', 'Annen innskutt egenkapital'); }, kind: 'innskutt', from: 2030, to: 2039 },
  { key: 'fond', get label() { return t('rce.cat.funds', 'Fond'); }, kind: 'opptjent', from: 2040, to: 2049 },
  { key: 'opptjent', get label() { return t('rce.cat.otherEarned', 'Annen opptjent egenkapital'); }, kind: 'opptjent', from: 2050, to: 2054 },
  { key: 'avsatt_utbytte', get label() { return t('rce.cat.dividendProvision', 'Avsatt utbytte ført mot egenkapital'); }, kind: 'opptjent', from: 2055, to: 2059 },
  { key: 'privat', get label() { return t('rce.cat.privateAccount', 'Privatkonto, uttak og innskudd'); }, kind: 'opptjent', from: 2060, to: 2079 },
  { key: 'udekket_tap', get label() { return t('rce.cat.uncoveredLoss', 'Udekket tap / negativ egenkapital'); }, kind: 'opptjent', from: 2080, to: 2089 },
  { key: 'annen_egenkapital', get label() { return t('rce.cat.otherEquity', 'Annen egenkapital'); }, kind: 'opptjent', from: 2090, to: 2099 },
];

const EQUITY_RECON_FROM = 2000;
const EQUITY_RECON_TO = 2099;

const equityReconCodeNumber = (code) => {
  const parsed = Number(String(code || '').trim().slice(0, 4));
  return Number.isFinite(parsed) ? parsed : null;
};

const equityReconIsEquityCode = (code) => {
  const number = equityReconCodeNumber(code);
  return number !== null && number >= EQUITY_RECON_FROM && number <= EQUITY_RECON_TO;
};

const equityReconCategoryFor = (code) => {
  const number = equityReconCodeNumber(code);
  if (number === null) return null;
  return EQUITY_RECON_CATEGORIES.find((entry) => number >= entry.from && number <= entry.to) || null;
};

// ------------------------------------------------------------------
// Kontoene
// ------------------------------------------------------------------
// Egenkapitalkontoene i saldobalansen, pluss kontoene byrået selv har lagt til
// området. En importert kontoplan kan bruke andre numre, og da er det området
// som bestemmer hva som hører til egenkapitalen.
const equityReconAccountRows = ({ trial, areaAccounts, lines }) => {
  const wanted = new Map();
  for (const [code, account] of trial) {
    if (equityReconIsEquityCode(code)) wanted.set(code, account);
  }
  for (const account of safeArray(areaAccounts)) {
    const code = String(account.accountId || '').trim();
    if (code && !wanted.has(code)) wanted.set(code, trial.get(code) || null);
  }

  const movement = new Map();
  for (const line of safeArray(lines)) {
    if (!wanted.has(line.accountCode)) continue;
    const entry = movement.get(line.accountCode) || { debet: 0, kredit: 0, count: 0 };
    if (num(line.amount) >= 0) entry.debet += num(line.amount);
    else entry.kredit += -num(line.amount);
    entry.count += 1;
    movement.set(line.accountCode, entry);
  }

  return [...wanted.keys()].sort().map((code) => {
    const trialRow = wanted.get(code);
    const areaAccount = safeArray(areaAccounts).find((row) => String(row.accountId) === code) || null;
    const entry = movement.get(code) || { debet: 0, kredit: 0, count: 0 };
    // Fortegnet snus her, én gang. Alt nedenfor regner med egenkapital som et
    // positivt tall.
    const opening = trialRow
      ? equityReconRound(-num(trialRow.inn))
      : equityReconRound(-num(areaAccount?.openingBalance));
    const closing = trialRow
      ? equityReconRound(-num(trialRow.ut))
      : equityReconRound(-num(areaAccount?.bookedBalance));
    const category = equityReconCategoryFor(code);
    const computed = equityReconRound(opening + entry.kredit - entry.debet);
    return {
      code,
      name: trialRow?.name || areaAccount?.accountName || '',
      categoryKey: category ? category.key : 'annen_egenkapital',
      categoryLabel: category ? category.label : t('rce.cat.otherEquity', 'Annen egenkapital'),
      kind: category ? category.kind : 'opptjent',
      opening,
      // Tilgang øker egenkapitalen (kredit), avgang reduserer den (debet).
      tilgang: equityReconRound(entry.kredit),
      avgang: equityReconRound(entry.debet),
      closing,
      change: equityReconRound(closing - opening),
      count: entry.count,
      computed,
      // Henger ikke IB + bevegelse sammen med UB, mangler perioden posteringer
      // som ikke kom med i bilagene. Det skal ses, ikke skjules.
      drift: equityReconRound(computed - closing),
      empty: !opening && !closing && !entry.debet && !entry.kredit,
    };
  });
};

// ------------------------------------------------------------------
// Egenkapitaloppstillingen
// ------------------------------------------------------------------
// Én rad per egenkapitalkategori: inngående, tilgang, avgang, beregnet utgående
// og bokført. Beregnet utgående er inngående pluss bevegelsen fra hovedboken;
// bokført er saldobalansens egen utgående. Er de to ulike, er det bilagene som
// mangler, ikke avstemmingen som er feil.
const equityReconStatement = (accountRows) => {
  const byCategory = new Map();
  for (const row of accountRows) {
    const current = byCategory.get(row.categoryKey) || {
      key: row.categoryKey,
      label: row.categoryLabel,
      kind: row.kind,
      inn: 0,
      tilgang: 0,
      avgang: 0,
      beregnetUt: 0,
      bokfort: 0,
      accounts: [],
    };
    current.inn = equityReconRound(current.inn + row.opening);
    current.tilgang = equityReconRound(current.tilgang + row.tilgang);
    current.avgang = equityReconRound(current.avgang + row.avgang);
    current.beregnetUt = equityReconRound(current.beregnetUt + row.computed);
    current.bokfort = equityReconRound(current.bokfort + row.closing);
    current.accounts.push(row.code);
    byCategory.set(row.categoryKey, current);
  }

  const order = EQUITY_RECON_CATEGORIES.map((entry) => entry.key);
  return [...byCategory.values()]
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
    .map((row) => {
      const difference = equityReconRound(row.beregnetUt - row.bokfort);
      return {
        ...row,
        difference,
        empty: !row.inn && !row.tilgang && !row.avgang && !row.bokfort,
        status: Math.abs(difference) > 0.5 ? 'differanse' : 'avstemt',
      };
    });
};

// ------------------------------------------------------------------
// Endringer i egenkapitalen
// ------------------------------------------------------------------
// Hver bevegelse på en egenkapitalkonto i perioden, samlet per bilag. Typen
// leses av bilagets egen kilde og av motkontoen — ikke av teksten på linjen. En
// nøkkelordsregel på fritekst ville gitt en klassifisering uten grunnlag.
const EQUITY_RECON_DIVIDEND_DEBT = ['2800', '2801', '2802'];

const equityReconCounterAccounts = (voucherLines, equityCodes) => {
  const counters = [];
  for (const line of voucherLines) {
    if (equityCodes.has(line.accountCode)) continue;
    if (counters.some((entry) => entry.code === line.accountCode)) continue;
    counters.push({ code: line.accountCode, name: line.accountName || '' });
  }
  return counters;
};

const equityReconTransactionType = ({ sourceType, counters, effect }) => {
  const codes = counters.map((entry) => entry.code);
  const number = (code) => equityReconCodeNumber(code);
  const hasDividendDebt = codes.some((code) => EQUITY_RECON_DIVIDEND_DEBT.includes(code));
  const hasBank = codes.some((code) => number(code) !== null && number(code) >= 1900 && number(code) <= 1999);
  const hasOwnerDebt = codes.some((code) => ['2250', '2910', '1565'].includes(code));
  const hasResult = codes.some((code) => number(code) !== null && number(code) >= 3000);
  if (sourceType === 'yearEndEntry') {
    if (hasDividendDebt) return { key: 'utbytte', label: t('rce.type.dividendProvision', 'Avsatt utbytte') };
    if (hasResult) return { key: 'resultatdisponering', label: t('rce.type.profitAllocation', 'Resultatdisponering') };
    return { key: 'arsoppgjor', label: t('rce.type.yearEndEntry', 'Årsoppgjørspostering mot egenkapital') };
  }
  if (hasDividendDebt) return { key: 'utbytte', label: t('rce.type.distribution', 'Utdeling til eier') };
  if (hasOwnerDebt) return { key: 'eiertransaksjon', label: t('rce.type.ownerCurrentAccount', 'Transaksjon mot mellomregning med eier') };
  // Retningen mot bank sier hvilken vei pengene gikk, ikke hva transaksjonen
  // ER. Om et uttak er utbytte, lønn, tilbakebetaling av innskutt kapital eller
  // et lån til aksjonær avgjøres av vedtaket og vilkårene, ikke av fortegnet —
  // derfor er etiketten en beskrivelse av føringen, og forholdet flagges for
  // vurdering i stedet for å bli klassifisert her.
  if (hasBank) {
    return effect >= 0
      ? { key: 'innskudd', label: t('rce.type.receipt', 'Innbetaling ført mot egenkapital') }
      : { key: 'uttak', label: t('rce.type.payment', 'Utbetaling ført mot egenkapital') };
  }
  return { key: 'direktefort', label: t('avbe.tile.directPosting', 'Direkteføring mot egenkapital') };
};

const equityReconTransactions = ({ lines, equityCodes, decision }) => {
  const byVoucher = new Map();
  for (const line of safeArray(lines)) {
    const entry = byVoucher.get(line.voucherId) || { lines: [], equity: [] };
    entry.lines.push(line);
    if (equityCodes.has(line.accountCode)) entry.equity.push(line);
    byVoucher.set(line.voucherId, entry);
  }

  const rows = [];
  for (const [voucherId, entry] of byVoucher) {
    if (!entry.equity.length) continue;
    const first = entry.equity[0];
    // Effekten på egenkapitalen er kreditsummen minus debetsummen. Fortegnet er
    // det samme som oppstillingens: pluss øker egenkapitalen.
    const effect = equityReconRound(entry.equity.reduce((sum, line) => sum - num(line.amount), 0));
    if (!effect) continue;
    const counters = equityReconCounterAccounts(entry.lines, equityCodes);
    const type = equityReconTransactionType({ sourceType: first.sourceType, counters, effect });
    const categories = [...new Set(entry.equity.map((line) => {
      const category = equityReconCategoryFor(line.accountCode);
      return category ? category.label : t('rce.cat.otherEquity', 'Annen egenkapital');
    }))];
    // Beslutningsgrunnlaget gjelder utdelinger og resultatdisponering. Det
    // kommer fra generalforsamlingen som er registrert i appen — ikke fra en
    // dato utledet av bilaget.
    const needsDecision = type.key === 'utbytte' || type.key === 'resultatdisponering';
    const decided = needsDecision ? decision : null;
    rows.push({
      id: 'ek-trans-' + voucherId,
      voucherId,
      voucherNumber: first.voucherNumber || '',
      date: first.date,
      sourceType: first.sourceType || '',
      type: type.label,
      typeKey: type.key,
      description: first.voucherName || first.description || type.label,
      amount: effect,
      categories,
      counters,
      accounts: entry.equity.map((line) => line.accountCode),
      needsDecision,
      // Et årsoppgjørsbilag som rører en resultatkonto er periodens resultat på
      // vei inn i egenkapitalen. Det avgjør om resultatet skal legges til én
      // gang til i beregnet egenkapital, eller om det allerede står der.
      movesResult: first.sourceType === 'yearEndEntry'
        && counters.some((counter) => (equityReconCodeNumber(counter.code) || 0) >= 3000),
      decisionDate: decided && decided.date ? decided.date : null,
      decisionSource: decided && decided.date ? decided.source : '',
      documented: needsDecision ? Boolean(decided && decided.documented) : true,
    });
  }

  return rows.sort((a, b) => String(a.date).localeCompare(String(b.date)));
};

Object.assign(window, {
  equityReconRound,
  equityReconMonthRange,
  EQUITY_RECON_CATEGORIES,
  equityReconCodeNumber,
  equityReconIsEquityCode,
  equityReconCategoryFor,
  equityReconAccountRows,
  equityReconStatement,
  equityReconTransactions,
  equityReconTransactionType,
});
